/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Content } from "../../_theme/layout/components/Content";
import { useSnackbar } from "../../_theme/context/SnackbarContext";
import { getProjectStageLink } from "../../utils/config";
import { KTIcon } from "../../_theme/helpers";
import PanopticEvaluatorModal from "../modules/Dashboard/components/Evaluation/PanopticEvaluatorModal";
import PanopticPrincipleModal from "../modules/Dashboard/components/Evaluation/PanopticPrincipleModal";
import ScoreRangeSlider from "../modules/apps/components/ScoreRangeSlider";
import {
  ApiResponse,
  EvaluationChartData,
  IDepartment,
  IPanopticEvaluationScoreCardList,
  ISector,
  UserScore,
} from "../modules/core/_models";
import {
  exportPanopticEvaluationProjects,
  getPanopticEvaluationProjects,
  getPanopticEvaluatorIndividualScores,
  getPanopticEvaluatorQuestionWeightedScores,
} from "../modules/core/_panoptic_evaluation";
import { Link, useNavigate } from "react-router-dom";
import { HtmlTruncateWithTooltip } from "../modules/apps/components/HtmlTruncateWithTooltip";
import { CustomDateFormate } from "../../utils/date";
import {
  getDepartments,
  getSectorSubSectors,
} from "../modules/core/_request";
import CustomFormSearchSelect from "../modules/apps/components/CustomFormSearchSelect";
import CustomFormInput from "../modules/apps/components/CustomFormInput";
import CustomFormDateInput from "../modules/apps/components/CustomDateInput";
import SimpleLoader from "../modules/apps/components/SimpleLoader";
import {
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  ArrowLeft,
  BarChart3,
  Briefcase,
  Building,
  Calendar,
  ChartColumn,
  Filter,
  Grid2x2,
  Search,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import AppModal from "../modules/apps/components/AppModal";
import CustomDynamicTable from "../modules/apps/components/CustomDynamicTable";
import { use } from "echarts";
import { getApiErrorMessage } from "../../utils/utils";
import { isAbortedError } from "../../utils/global";

interface IFilter {
  sectorId: number;
  departmentId: number;
  fromDate: string;
  toDate: string;
  scoreRange: [number, number];
  search: string;
}

const defaultFilterValues: IFilter = {
  sectorId: 0,
  departmentId: 0,
  fromDate: "",
  toDate: "",
  search: "",
  scoreRange: [0, 100],
};

const PanopticEvaluationScoreCard = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [tableLoading, setTableLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [projects, setProjects] = useState<IPanopticEvaluationScoreCardList[]>(
    [],
  );
  const [openModal, setOpenModal] = useState<
    "none" | "evaluator" | "principle"
  >("none");
  const [selectedProject, setSelectedProject] =
    useState<IPanopticEvaluationScoreCardList | null>(null);
  const [departments, setDepartments] = useState<IDepartment[]>([]);
  const [sectors, setSectors] = useState<ISector[]>([]);

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [userScore, setUserScore] = useState<UserScore[]>([]);
  const [data, setData] = useState<EvaluationChartData | null>(null);

  const [filters, setFilters] = useState<IFilter>(defaultFilterValues);
  const [appliedFilters, setAppliedFilters] =
    useState<IFilter>(defaultFilterValues);

  const projectsAbortRef = useRef<AbortController | null>(null);
  const filtersAbortRef = useRef<AbortController | null>(null);
  const weightedScoresAbortRef = useRef<AbortController | null>(null);
  const individualScoresAbortRef = useRef<AbortController | null>(null);

  const sectorColors = useMemo(
    () => ({
      Social: "bg-gradient-primary-light border-primary text-primary",
      Ecological: "bg-gradient-success-light border-success text-success",
      Energy: "bg-gradient-danger-light border-danger text-danger",
      Economic: "bg-gradient-warning-light border-warning text-warning",
      Technology: "bg-gradient-info-light border-info text-info",
    }),
    [],
  );


  const handleGetPanopticEvaluatorQuestionWeightedScores =
    useCallback(async () => {
      if (!selectedProject?.project_id || openModal !== "evaluator") {
        return;
      }
      weightedScoresAbortRef.current?.abort();
      const controller = new AbortController();
      weightedScoresAbortRef.current = controller;

      setModalLoading(true);
      try {
        const response = await getPanopticEvaluatorQuestionWeightedScores(
          selectedProject?.project_id,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        if (response.status === "ok") {
          setData(response.data);
        } else {
          showSnackbar(response.message, "danger");
        }
      } catch (error) {
        if (isAbortedError(error)) return;
        console.error(error);
      } finally {
        if (!controller.signal.aborted) setModalLoading(false);
      }
    }, [selectedProject?.project_id, openModal, showSnackbar]);

  const handleGetPanopticEvaluatorIndividualScores = useCallback(async () => {
    if (!selectedProject?.project_id || openModal !== "principle") {
      return;
    }
    individualScoresAbortRef.current?.abort();
    const controller = new AbortController();
    individualScoresAbortRef.current = controller;

    setModalLoading(true);
    try {
      const response = await getPanopticEvaluatorIndividualScores(
        selectedProject?.project_id,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      if (response.status === "ok") {
        setUserScore(response.data);
      } else {
        showSnackbar(response.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error(error);
    } finally {
      if (!controller.signal.aborted) setModalLoading(false);
    }
  }, [selectedProject?.project_id, openModal, showSnackbar]);

  const handleExportEvaluationScoreReport = useCallback(
    async (type: "excel" | "pdf") => {
      if (!type) {
        return;
      }
      setExportLoading(true);
      try {
        const response = await exportPanopticEvaluationProjects(type);
        if (response.status === "ok") {
          const fileUrl = response.data.download_url;
          window.open(fileUrl, "_blank");
        } else {
          showSnackbar(response.message, "danger");
        }
      } catch (error) {
        console.error(error);
      } finally {
        setExportLoading(false);
      }
    },
    [ showSnackbar],
  );

  const getScoreClass = (score: number) => {
    if (score < 50) return "bg-light-danger text-danger border-danger";
    if (score < 80) return "bg-light-warning text-warning border-warning";
    return "bg-light-success text-success border-success";
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: "Project Name",
        width: "350px",
        render: (info: IPanopticEvaluationScoreCardList) => {
          return (
            <div className="d-flex align-items-center gap-2">
              <div
                className={`border-end border-3 h-100 py-5 border-${getScoreClass(Number(info.eval_percentage))}`}
              ></div>
              <Link to={`${getProjectStageLink(info.project_id)}`}>
                {info.name.length > 60 ? (
                  <HtmlTruncateWithTooltip
                    html={info.name as string}
                    limit={60}
                    className="text-gray-700 fw-bold text-hover-primary fs-6"
                  />
                ) : (
                  <p className="text-gray-700 fw-bold text-hover-primary mb-0 fs-6">
                    {info.name}
                  </p>
                )}
              </Link>
            </div>
          );
        },
      },
      {
        key: "sector_name",
        label: "Sector",
        align: "center",
        render: (info: IPanopticEvaluationScoreCardList) => {
          return (
            <p
              className={`border rounded-pill fw-semibold mb-0 py-1 px-2 fs-7 ${sectorColors[info?.sector_name?.split(" ")[0] as keyof typeof sectorColors]}`}
            >
              {info.sector_name}
            </p>
          );
        },
      },
      {
        key: "department_name",
        label: "Department",
        width: "280px",
      },
      {
        key: "project_owner",
        label: "Project Owner",
        render: (info: IPanopticEvaluationScoreCardList) => {
          return (
            <div className="d-flex align-items-center gap-2">
              <div
                className="text-white rounded-circle d-flex justify-content-center align-items-center today-btn flex-shrink-0"
                style={{
                  width: "32px",
                  height: "32px",
                  minWidth: "32px",
                  minHeight: "32px",
                }}
              >
                {info.project_owner.split(" ")[0]?.charAt(0)}
                {info.project_owner.split(" ")[1]?.charAt(0)}
              </div>

              <p className="text-gray-700 fs-6 mb-0">
                {info.project_owner}
              </p>
            </div>
          );
        },
      },
      {
        key: "eval_due_date",
        label: "Due Date",
        align: "center",
        width: "140px",
        render: (info: IPanopticEvaluationScoreCardList) => {
          return (
            <p className="text-gray-700 mb-0 fs-6">
              {CustomDateFormate(info.eval_due_date)}
            </p>
          );
        },
      },
      {
        key: "eval_completed_date",
        label: "Evaluation completion Date",
        align: "center",
        width: "140px",
        render: (info: IPanopticEvaluationScoreCardList) => {
          return (
            <p className="text-gray-700 mb-0 fs-6">
              {CustomDateFormate(info.eval_completed_date)}
            </p>
          );
        },
      },
      {
        key: "eval_percentage",
        label: "Score",
        align: "center",
        render: (info: IPanopticEvaluationScoreCardList) => {

          return (
            <p className={`border rounded-pill fw-semibold fw-bold mb-0 fs-7 py-1 px-2 ${getScoreClass(Number(info.eval_percentage))}`}>
              {info.eval_percentage}%
            </p>
          );
        },
      },
      {
        key: "graph",
        label: "Graph",
        align: "center",
        render: (info: IPanopticEvaluationScoreCardList) => {
          const handleEvaluatorView = () => {
            setSelectedProject(info);
            setOpenModal("evaluator");
          };
          const handlePrincipleView = () => {
            setSelectedProject(info);
            setOpenModal("principle");
          };
          return (
            <div className="d-flex justify-content-end flex-shrink-0">
              <OverlayTrigger overlay={<Tooltip id="tooltip">Panoptic Principle graph</Tooltip>}>
                <button
                  onClick={handleEvaluatorView}
                  className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                >
                  <KTIcon iconName="star" className="fs-2" />
                </button>
              </OverlayTrigger>
              <OverlayTrigger overlay={<Tooltip id="tooltip">Evaluator wise Score</Tooltip>}>
                <button
                  onClick={handlePrincipleView}
                  className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                >
                  <KTIcon iconName="user-tick" className="fs-2" />
                </button>
              </OverlayTrigger>
            </div>
          );
        },
      },
    ],
    [
      selectedProject,
      openModal,
      sectorColors
    ],
  );

  const getEvaluationProjects = useCallback(async () => {
    projectsAbortRef.current?.abort();
    const controller = new AbortController();
    projectsAbortRef.current = controller;

    setTableLoading(true);
    try {
      const response = await getPanopticEvaluationProjects(
        appliedFilters.sectorId,
        appliedFilters.departmentId,
        appliedFilters.fromDate,
        appliedFilters.toDate,
        appliedFilters.search,
        appliedFilters.scoreRange,
        page,
        perPage,
        true,
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (response.status === "ok") {
        setProjects(response.data.projects);
        setTotal(response.data.pagination.total_records);
      } else {
        showSnackbar(response.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error(error);
      showSnackbar(getApiErrorMessage(error, "Error fetching panoptic evaluation projects"), "danger");
    } finally {
      if (!controller.signal.aborted) setTableLoading(false);
    }

  }, [appliedFilters, showSnackbar, page, perPage]);

  useEffect(() => {
    getEvaluationProjects();
    return () => projectsAbortRef.current?.abort();
  }, [getEvaluationProjects]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };
  const resetFilters = () => {
    setFilters(defaultFilterValues);
    setAppliedFilters(defaultFilterValues);
  };

  const fetchFilterData = useCallback(async () => {
    filtersAbortRef.current?.abort();
    const controller = new AbortController();
    filtersAbortRef.current = controller;

    try {
      setTableLoading(true);
      const [sectorResponse, departmentResponse] =
        (await Promise.all([
          getSectorSubSectors(controller.signal),
          getDepartments(controller.signal),
        ]));

      if (controller.signal.aborted) return;

      if (sectorResponse.status === "ok") {
        setSectors(sectorResponse.data.sectors);
      } else {
        sectorResponse.message &&
          showSnackbar(sectorResponse.message, "danger");
      }

      if (departmentResponse.status === "ok") {
        setDepartments(departmentResponse.data.departments);
      } else {
        departmentResponse.message &&
          showSnackbar(departmentResponse.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch Filtered projects:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch Filtered projects:"), "danger");
    } finally {
      if (!controller.signal.aborted) setTableLoading(false);
    }
  }, [showSnackbar]);

  const scoreStats = useMemo(() => {
    const values = projects
      .map((p) => Number(p.eval_percentage))
      .filter((n) => !isNaN(n));

    return {
      min: values.length ? Math.min(...values) : "0",
      max: values.length ? Math.max(...values) : "0",
    };
  }, [projects]);

  const closeModal = () => {
    setOpenModal("none");
    setSelectedProject(null);
    setData(null);
    setUserScore([]);
  };

  useEffect(() => {
    fetchFilterData();
    return () => filtersAbortRef.current?.abort();
  }, [fetchFilterData]);

  useEffect(() => {
    handleGetPanopticEvaluatorQuestionWeightedScores();
    return () => weightedScoresAbortRef.current?.abort();
  }, [handleGetPanopticEvaluatorQuestionWeightedScores]);

  useEffect(() => {
    handleGetPanopticEvaluatorIndividualScores();
    return () => individualScoresAbortRef.current?.abort();
  }, [handleGetPanopticEvaluatorIndividualScores]);

  return (
    <>
      <Content>
        <div className="card my-5 ">
          {tableLoading || modalLoading || exportLoading && (
            <SimpleLoader
              className={"bg-black-30 z-2"}
              text="Loading panoptic evaluation score card..."
            />
          )}
          <div className="card-header bg-light d-flex align-items-center border-0 p-6">
            <div className="card-title d-flex align-items-center gap-4 m-0">
              <div className="p-3 rounded-4 bg-gradient-primary">
                <BarChart3 size={25} className="text-white" />
              </div>
              <div>
                {/* <PageTitle sitetitle={envConfig.APP_GOV_TITLE} breadcrumbs={breadCrumbs}>Panoptic Evaluation Score Card</PageTitle>
                                <ManageProjectToolbar customPadding='py-0' extraClass='p-0' rightSection={<></>} /> */}
                <p className="card-title fs-2 m-0 ">
                  Panoptic Evaluation Score Card
                </p>
                <p className="mb-0 fs-6 text-gray-700">
                  Comprehensive performance overview across all evaluated
                  projects
                </p>
              </div>
            </div>
            <div className="gap-3 d-flex">
              <OverlayTrigger
                overlay={
                  <Tooltip id="tooltip">
                    {showFilter ? "Hide" : "Show"} Filters
                  </Tooltip>
                }
              >
                <Button
                  className={`btn ${showFilter ? "btn-secondary" : "btn-primary"} btn-icon p-0`}
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <Filter size={16} />
                </Button>
              </OverlayTrigger>
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle variant="light-primary" className="py-0">
                  Export
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => handleExportEvaluationScoreReport("excel")}
                  >
                    Export to Excel
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleExportEvaluationScoreReport("pdf")}
                  >
                    Export to PDF
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button
                className="btn btn-secondary py-0"
                onClick={() => navigate("/dashboard/reports")}
              >
                <ArrowLeft className="me-2" size={16} />
                Back
              </Button>
            </div>
          </div>

          <div className={`card-body p-3`}>
            {showFilter && (
              <div className="card shadow-sm">
                <div className="card-body rounded-4 p-6">
                  {/* Row 1 */}
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label d-flex align-items-center gap-2">
                        <Grid2x2 size={16} className="text-muted" />
                        Sector
                      </label>
                      <CustomFormSearchSelect
                        value={filters.sectorId}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            sectorId: value as number,
                          }))
                        }
                        name="sector_id"
                        options={[
                          { label: "All", value: 0 },
                          ...sectors.map((sector) => ({
                            label: sector.name,
                            value: sector.sector_id,
                          })),
                        ]}
                        placeholder="Select sector"
                        size="sm"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label d-flex align-items-center gap-2">
                        <Building size={16} className="text-muted" />
                        Department
                      </label>
                      <CustomFormSearchSelect
                        value={filters.departmentId}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            departmentId: value as number,
                          }))
                        }
                        name="department_id"
                        options={[
                          { label: "All", value: 0 },
                          ...departments.map((department) => ({
                            label: department.department_name,
                            value: department.department_id,
                          })),
                        ]}
                        placeholder="Select department"
                        size="sm"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label d-flex align-items-center gap-2">
                        <ChartColumn size={16} className="text-muted" />
                        Score Range
                      </label>
                      <CustomFormSearchSelect
                        value={`${filters.scoreRange[0]}-${filters.scoreRange[1]}`}
                        onChange={(value) => {
                          const selected = value as string;
                          const scoreRangeMap: Record<string, [number, number]> = {
                            "0-100": [0, 100],
                            "0-49": [0, 49],
                            "50-79": [50, 79],
                            "80-100": [80, 100],
                          };
                          const range = scoreRangeMap[selected] || [0, 100];
                          setFilters((prev) => ({
                            ...prev,
                            scoreRange: range,
                          }));
                        }}
                        name="score_range"
                        options={[
                          { label: "All", value: "0-100" },
                          { label: "0-49", value: "0-49" },
                          { label: "50-79", value: "50-79" },
                          { label: "80-100", value: "80-100" },
                        ]}
                        placeholder="Select score range"
                        size="sm"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label d-flex align-items-center gap-2">
                        <Search size={16} className="text-muted" />
                        Search
                      </label>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <Search size={15} className="text-muted" />
                        </InputGroup.Text>
                        <CustomFormInput
                          value={filters.search}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              search: e.target.value,
                            }))
                          }
                          name="search"
                          placeholder="Project name"
                        />
                      </InputGroup>
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="row g-3 mt-3">
                    <div className="col-md-3">
                      <label className="form-label d-flex align-items-center gap-2">
                        <Calendar size={16} className="text-muted" />
                        From Date
                      </label>
                      <CustomFormDateInput
                        value={filters.fromDate ? new Date(filters.fromDate) : null}
                        onChange={(date) =>
                          setFilters((prev) => ({
                            ...prev,
                            fromDate: date as string,
                          }))
                        }
                        options={{
                          dateFormat: "d-m-Y",
                          maxDate: filters.toDate ? new Date(filters.toDate) : undefined,
                        }}
                        size="sm"
                      />
                    </div>

                    <div className="col-md-3">
                      <label className="form-label d-flex align-items-center gap-2">
                        <Calendar size={16} className="text-muted" />
                        To Date
                      </label>
                      <CustomFormDateInput
                        value={filters.toDate ? new Date(filters.toDate) : null}
                        onChange={(date) =>
                          setFilters((prev) => ({
                            ...prev,
                            toDate: date as string,
                          }))
                        }
                        options={{
                          dateFormat: "d-m-Y",
                          minDate: filters.fromDate ? new Date(filters.fromDate) : undefined,
                        }}
                        size="sm"
                      />
                    </div>

                    <div className="col-md-4 pe-5">
                      <label className="form-label d-flex align-items-center gap-2">
                        <ChartColumn size={16} className="text-muted" />
                        Score Range Slider
                      </label>
                      <ScoreRangeSlider
                        value={filters.scoreRange}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            scoreRange: value,
                          }))
                        }
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">&nbsp;</label>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary flex-grow-1 py-2"
                          onClick={applyFilters}
                        >
                          Apply
                        </button>
                        <button
                          className="btn bg-gray-200 flex-grow-1 py-2"
                          onClick={resetFilters}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="row gx-4 gy-4 p-3 bg-white">
              <Col md={4} className="ps-0">
                <div className="p-4 rounded bg-gradient-primary-light h-100 d-flex justify-content-between align-items-center gap-2">
                  <div>
                    <p className="mb-0 fs-7 fw-semibold text-gray-700">
                      TOTAL PROJECTS
                    </p>
                    <p className="fw-bold fs-1 mb-0 text-gray-900">
                      {projects.length}
                    </p>
                  </div>
                  <div className="bg-white shadow-sm p-3 rounded-4">
                    <Briefcase size={24} className="text-primary" />
                  </div>
                </div>
              </Col>

              <Col md={4}>
                <div className="p-4 rounded bg-gradient-danger-light h-100 d-flex justify-content-between align-items-center gap-2">
                  <div>
                    <p className="mb-0 fs-7 fw-semibold text-gray-700">
                      LOWEST SCORE
                    </p>
                    <p className="fw-bold fs-1 mb-0 text-gray-900">
                      {scoreStats.min}%
                    </p>
                  </div>
                  <div className="bg-white shadow-sm p-3 rounded-4">
                    <TrendingDown size={24} className="text-danger" />
                  </div>
                </div>
              </Col>

              <Col md={4} className="pe-0">
                <div className="p-4 rounded bg-gradient-success-light h-100 d-flex justify-content-between align-items-center gap-2">
                  <div>
                    <p className="mb-0 fs-7 fw-semibold text-gray-700">
                      HIGHEST SCORE
                    </p>
                    <p className="fw-bold fs-1 mb-0 text-gray-900">
                      {scoreStats.max}%
                    </p>
                  </div>
                  <div className="bg-white shadow-sm p-3 rounded-4">
                    <TrendingUp size={24} className="text-success" />
                  </div>
                </div>
              </Col>
            </div>

            <div className="card">
              <div className="card-body p-3 shadow-sm">
                <CustomDynamicTable
                  tableClassName="table table-striped table-row-bordered table-hover align-middle mb-0 gs-3"
                  columnClassName="border-0 fw-bold"
                  columns={columns}
                  data={projects}
                  pagination={{
                    currentPage: page,
                    totalCount: total,
                    pageSize: perPage,
                    onPageChange: (p) => setPage(p),
                    onPageSizeChange: (size) => {
                      setPerPage(size);
                      setPage(1);
                    },
                  }}
                />
              </div>
            </div>
          </div>
          {openModal === "evaluator" && (
            <AppModal
              show={true}
              title={
                selectedProject?.name || "Panoptic Evaluation Evaluator Graph"
              }
              onClose={closeModal}
              showLoader={modalLoading}
              showCancelButton={false}
              width="1400px"
            >
              <PanopticEvaluatorModal data={data} chartheight={300} />
            </AppModal>
          )}
          {openModal === "principle" && (
            <PanopticPrincipleModal
              show={true}
              onClose={closeModal}
              data={userScore}
              title={selectedProject?.name}
            />
          )}
        </div>
      </Content>
    </>
  );
};

export { PanopticEvaluationScoreCard };
